import { useMemo, useCallback, memo } from 'react';
import BookingGridBase from '../common/BookingGridBase';
import TimeLineLasertag from './TimeLineLasertag';
import BookingCardLasertag from './BookingCardLasertag';
import BookingCardLasertagv2 from './BookingCardLasertagv2';
import dayjs from 'dayjs';

function GridLasertag({
  bookings,
  rowCount,
  date,
  slotCapacity,
  updateBooking,
  handleOpenModal,
  capacity,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
}) {
  // Derived synchronously from date – no double-render from useEffect+setState
  const slotMode = useMemo(
    () => date.isSame(dayjs(), 'day') || date.isBefore(dayjs(), 'day'),
    [date]
  );

  const isAgeSplit = entertainment?.ltCapacityMode === 'ageSplit';
  const slotGroupTypes = capacity?.slotGroupTypes;

  const calcAttendance = useCallback(
    list => {
      const times = Array.from({ length: (24 - 9) * 2 + 1 }, (_, idx) => {
        const h = 9 + Math.floor(idx / 2);
        const m = idx % 2 === 0 ? '00' : '30';
        const slotGroupType = slotGroupTypes?.[`${h}:${m}`];
        const base = {
          isFull: idx % 2 === 0,
          time: `${h}:${m}`,
          qualifiedTime: new Date(date.get('year'), date.get('month'), date.get('date'), h, m),
          i: idx,
          hasAdults: false,
          hasKids: false,
          kidsCount: 0,
          adultsCount: 0,
          ...(slotGroupType !== undefined ? { slotGroupType } : {}),
        };
        if (slotMode) {
          if (isAgeSplit) {
            const kCap = slotGroupType === 'adults' ? 0 : slotCapacity;
            const aCap = slotGroupType === 'kids' ? 0 : slotCapacity;
            return { ...base, s0: 0, frees0: slotCapacity, s1: 0, frees1: slotCapacity, freesKids: kCap, freesAdults: aCap, totalKidsCapacity: kCap, totalAdultsCapacity: aCap };
          }
          return { ...base, s0: 0, frees0: slotCapacity, s1: 0, frees1: slotCapacity };
        }
        if (isAgeSplit) {
          const totalCap = slotCapacity * 2;
          const kCap = slotGroupType === 'adults' ? 0 : (slotGroupType === 'kids' ? totalCap : slotCapacity);
          const aCap = slotGroupType === 'kids' ? 0 : (slotGroupType === 'adults' ? totalCap : slotCapacity);
          return { ...base, s0: 0, frees0: totalCap, freesKids: kCap, freesAdults: aCap, totalKidsCapacity: kCap, totalAdultsCapacity: aCap };
        }
        return { ...base, s0: 0, frees0: slotCapacity * 2 };
      });

      for (const bk of list) {
        const { bookingType } = bk;
        const isBlocker = bk.status === 'blocked';
        const startKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const endKey = bookingType === 'RegiondoBooking' ? 'eventEnd' : 'endTime';
        const compStart = new Date(bk[startKey]);
        const compEnd = new Date(bk[endKey]);
        const count = bk.participantCount;
        const groupID = bk.ltSpecific?.playerGroupID;
        const groupType = bk.ltSpecific?.groupType;

        for (let t of times) {
          if (compStart <= t.qualifiedTime && compEnd > t.qualifiedTime) {
            if (!isBlocker) {
              if (groupType === 'adults') t.hasAdults = true;
              if (groupType === 'kids') t.hasKids = true;
            }

            if (slotMode) {
              if (isBlocker && isAgeSplit) {
                const s0IsKids = t.slotGroupType !== 'adults';
                const s1IsKids = t.slotGroupType === 'kids';
                const bothSameGroup = (s0IsKids && s1IsKids) || (!s0IsKids && !s1IsKids);

                if (bothSameGroup) {
                  const matchesGroup =
                    (groupType === 'kids' && s0IsKids) ||
                    (groupType === 'adults' && !s0IsKids) ||
                    !groupType;
                  if (matchesGroup) {
                    t.frees0 -= Math.ceil(count / 2);
                    t.frees1 -= Math.floor(count / 2);
                  }
                } else {
                  if (groupType === 'kids') {
                    if (s0IsKids) t.frees0 -= count;
                    if (s1IsKids) t.frees1 -= count;
                  } else if (groupType === 'adults') {
                    if (!s0IsKids) t.frees0 -= count;
                    if (!s1IsKids) t.frees1 -= count;
                  } else {
                    t.frees0 -= count;
                    t.frees1 -= count;
                  }
                }
              } else if (isBlocker) {
                t.frees0 -= count;
                t.frees1 -= count;
              } else {
                const rawGroup = Number(groupID);
                const effectiveGroup = rawGroup === 0 || rawGroup === 1 ? rawGroup : 0;
                const key = `s${effectiveGroup}`;
                const freeKey = `frees${effectiveGroup}`;
                t[key] += count;
                t[freeKey] -= count;
              }
            } else {
              if (!isBlocker) {
                t.s0 += count;
              }
              t.frees0 -= count;
            }

            if (isAgeSplit && t.freesKids !== undefined) {
              if (groupType === 'kids') {
                if (!isBlocker) t.kidsCount += count;
                t.freesKids -= count;
              } else if (groupType === 'adults') {
                if (!isBlocker) t.adultsCount += count;
                t.freesAdults -= count;
              } else {
                t.freesKids -= count;
                t.freesAdults -= count;
              }
            }
          }
        }
      }
      return times;
    },
    [slotMode, slotCapacity, date, isAgeSplit, slotGroupTypes]
  );

  const renderTimeline = useCallback(
    ({ times, slotWidthPx, showTimeRow, showSlotRows, hideTimeRow }) => (
      <TimeLineLasertag
        times={times}
        slotMode={slotMode}
        renderAvailableSlots={true}
        slotWidth={slotWidthPx}
        showTimeRow={showTimeRow}
        showSlotRows={showSlotRows}
        hideTimeRow={hideTimeRow}
        entertainment={entertainment}
      />
    ),
    [slotMode, entertainment]
  );

  const renderBookingCard = useCallback(
    (bk, idx, rowHeight) =>
      bk.bookingType === 'RegiondoBooking' ? (
        <BookingCardLasertag
          key={bk._id}
          booking={bk}
          updateBookingState={updateBooking}
          slotMode={slotMode}
          renderAvailableSlots={true}
        />
      ) : (
        <BookingCardLasertagv2
          key={bk._id}
          booking={bk}
          updateBookingState={updateBooking}
          slotMode={slotMode}
          handleOpenModal={handleOpenModal}
          renderAvailableSlots={true}
          rowHeight={rowHeight}
          onBookingShift={onBookingShift}
          entertainment={entertainment}
          onDragStart={onDragStart}
          onDragActivated={onDragActivated}
          onDragEnd={onDragEnd}
          availableShiftTimes={availableShiftTimes}
          isShiftable={isShiftable}
        />
      ),
    [
      slotMode,
      updateBooking,
      handleOpenModal,
      onBookingShift,
      entertainment,
      onDragStart,
      onDragActivated,
      onDragEnd,
      availableShiftTimes,
      isShiftable,
    ]
  );

  return (
    <BookingGridBase
      bookings={bookings}
      rowCount={rowCount}
      date={date}
      slotCapacity={slotCapacity}
      calcAttendance={calcAttendance}
      capacityData={capacity}
      availableShiftTimes={availableShiftTimes}
      draggedBookingDuration={draggedBookingDuration}
      isLoadingShiftTimes={isLoadingShiftTimes}
      renderTimeline={renderTimeline}
      renderBookingCard={renderBookingCard}
      headerHeight={slotMode ? 74 : 50}
    />
  );
}

export default memo(GridLasertag);
